import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Group,
  Loader,
  Modal,
  Paper,
  Progress,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import {
  IconDownload,
  IconFile,
  IconFileTypePdf,
  IconFolder,
  IconFolderShare,
  IconMovie,
  IconPhoto,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { unwrap } from '../api/client';
import { browse, deleteFile, type ShelfFile, type ShelfFolder } from '../api/gen';
import { bytes, date, previewKind } from '../lib/format';
import { download, previewURL, upload } from '../lib/transfer';

// One folder of the shelf. The folder lives in the URL (?folder=), so a link
// to it can be passed on; whoever opens it sees it only if it is shared
// with them.
export function ShelfPage() {
  const [params, setParams] = useSearchParams();
  const folder = params.get('folder') ?? '';
  const open = (f: string) => setParams(f ? { folder: f } : {});
  const [preview, setPreview] = useState<ShelfFile | null>(null);

  const listing = useQuery({
    queryKey: ['shelf', folder],
    queryFn: () => unwrap(browse({ query: { folder } })),
  });

  return (
    <Stack gap="md" maw={1100} mx="auto">
      <Path folder={folder} onOpen={open} />
      {listing.isLoading ? <Loader size="sm" /> : null}
      {listing.error ? (
        <Alert color="red" variant="light" title="This folder cannot be opened">
          {(listing.error as Error).message}{' '}
          <Anchor component="button" onClick={() => open('')}>
            Back to the top
          </Anchor>
        </Alert>
      ) : null}
      {listing.data ? (
        <>
          <Heading data={listing.data} />
          {listing.data.permission === 'write' ? <UploadZone key={folder} folder={folder} /> : null}
          <Listing data={listing.data} onOpen={open} onPreview={setPreview} />
        </>
      ) : null}
      <Preview file={preview} onClose={() => setPreview(null)} />
    </Stack>
  );
}

function Path({ folder, onOpen }: { folder: string; onOpen: (f: string) => void }) {
  const parts = folder.split('/').filter(Boolean);
  return (
    <Breadcrumbs separator="/" separatorMargin={6}>
      <Anchor component="button" size="sm" onClick={() => onOpen('')}>
        Shelf
      </Anchor>
      {parts.map((p, i) => {
        const to = parts.slice(0, i + 1).join('/') + '/';
        return i === parts.length - 1 ? (
          <Text key={to} size="sm" fw={600} ff="monospace">
            {p}
          </Text>
        ) : (
          <Anchor key={to} component="button" size="sm" ff="monospace" onClick={() => onOpen(to)}>
            {p}
          </Anchor>
        );
      })}
    </Breadcrumbs>
  );
}

function Heading({ data }: { data: ShelfFolder }) {
  const name = data.folder.split('/').filter(Boolean).pop();
  const note: Record<ShelfFolder['permission'], string> = {
    write: 'You can upload and delete here.',
    read: 'Shared with you to watch and download.',
    none: data.folder ? 'The way down to what is shared with you.' : 'The folders shared with you.',
  };
  return (
    <Group justify="space-between" align="flex-end">
      <div>
        <Title order={3}>{name ?? (data.permission === 'none' ? 'Shared with me' : 'Shelf')}</Title>
        <Text size="sm" c="dimmed">
          {note[data.permission]}
        </Text>
      </div>
      <PermissionBadge permission={data.permission} />
    </Group>
  );
}

function PermissionBadge({ permission }: { permission: ShelfFolder['permission'] }) {
  if (permission === 'none') return null;
  return (
    <Badge color={permission === 'write' ? 'label' : 'gray'} c={permission === 'write' ? 'dark' : undefined} variant="filled">
      {permission}
    </Badge>
  );
}

function Listing({
  data,
  onOpen,
  onPreview,
}: {
  data: ShelfFolder;
  onOpen: (f: string) => void;
  onPreview: (f: ShelfFile) => void;
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (key: string) => unwrap(deleteFile({ query: { key } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['shelf', data.folder] }),
    onError: (e) => notifications.show({ color: 'red', title: 'Not deleted', message: (e as Error).message }),
  });

  if (data.folders.length === 0 && data.files.length === 0) {
    return (
      <Paper withBorder p="xl" ta="center">
        <Text c="dimmed">{data.permission === 'none' ? 'Nothing is shared with you yet.' : 'This folder is empty.'}</Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder>
      <Table highlightOnHover verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th visibleFrom="sm" w={110}>
              Size
            </Table.Th>
            <Table.Th visibleFrom="md" w={190}>
              Modified
            </Table.Th>
            <Table.Th w={90} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.folders.map((f) => (
            <Table.Tr key={f.key} style={{ cursor: 'pointer' }} onClick={() => onOpen(f.key)}>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  {f.permission === 'none' ? (
                    <IconFolder size={18} color="var(--mantine-color-gray-6)" />
                  ) : (
                    <IconFolderShare size={18} color="var(--mantine-color-enamel-6)" />
                  )}
                  <Text size="sm" ff="monospace">
                    {f.name}/
                  </Text>
                  {f.permission === 'write' && data.permission !== 'write' ? (
                    <Badge size="xs" color="label" c="dark">
                      write
                    </Badge>
                  ) : null}
                </Group>
              </Table.Td>
              <Table.Td visibleFrom="sm" />
              <Table.Td visibleFrom="md" />
              <Table.Td />
            </Table.Tr>
          ))}
          {data.files.map((f) => (
            <Table.Tr key={f.key}>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <FileIcon contentType={f.contentType} />
                  {previewKind(f.contentType) ? (
                    <Anchor component="button" size="sm" ff="monospace" ta="left" onClick={() => onPreview(f)}>
                      {f.name}
                    </Anchor>
                  ) : (
                    <Text size="sm" ff="monospace">
                      {f.name}
                    </Text>
                  )}
                </Group>
              </Table.Td>
              <Table.Td visibleFrom="sm">
                <Text size="sm" c="dimmed">
                  {bytes(f.size)}
                </Text>
              </Table.Td>
              <Table.Td visibleFrom="md">
                <Text size="sm" c="dimmed">
                  {date(f.modified)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap={4} justify="flex-end" wrap="nowrap">
                  <Tooltip label="Download">
                    <ActionIcon
                      variant="subtle"
                      aria-label={`Download ${f.name}`}
                      onClick={() =>
                        void download(f.key).catch((e) =>
                          notifications.show({ color: 'red', title: 'No download', message: (e as Error).message }),
                        )
                      }
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                  </Tooltip>
                  {data.permission === 'write' ? (
                    <Tooltip label="Delete">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Delete ${f.name}`}
                        loading={remove.isPending && remove.variables === f.key}
                        onClick={() => {
                          if (window.confirm(`Delete ${f.name} from the bucket? This cannot be undone.`)) remove.mutate(f.key);
                        }}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  ) : null}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {data.truncated ? (
        <Text size="xs" c="dimmed" p="xs">
          This folder holds more files than one page shows.
        </Text>
      ) : null}
    </Paper>
  );
}

function FileIcon({ contentType }: { contentType: string }) {
  const kind = previewKind(contentType);
  const props = { size: 18, color: 'var(--mantine-color-gray-6)' };
  if (kind === 'video') return <IconMovie {...props} />;
  if (kind === 'image') return <IconPhoto {...props} />;
  if (kind === 'pdf') return <IconFileTypePdf {...props} />;
  return <IconFile {...props} />;
}

interface Transfer {
  name: string;
  progress: number;
  error?: string;
}

// Files dropped here go straight to the bucket, one signed PUT each; the
// server signs and never sees the bytes.
function UploadZone({ folder }: { folder: string }) {
  const qc = useQueryClient();
  const [transfers, setTransfers] = useState<Transfer[]>([]);

  async function send(files: File[]) {
    setTransfers(files.map((f) => ({ name: f.name, progress: 0 })));
    await Promise.all(
      files.map(async (file, i) => {
        const set = (t: Partial<Transfer>) =>
          setTransfers((all) => all.map((x, j) => (j === i ? { ...x, ...t } : x)));
        try {
          await upload(folder + file.name, file, (progress) => set({ progress }));
        } catch (e) {
          set({ error: (e as Error).message });
        }
      }),
    );
    void qc.invalidateQueries({ queryKey: ['shelf', folder] });
  }

  return (
    <Stack gap="xs">
      <Dropzone onDrop={(files) => void send(files)} radius="md" p="md">
        <Group justify="center" gap="sm" mih={48} style={{ pointerEvents: 'none' }}>
          <IconUpload size={22} color="var(--mantine-color-enamel-6)" />
          <Text size="sm">
            Drop files here, or click to choose. They go straight to{' '}
            <Text span ff="monospace" bg="var(--mantine-color-label-1)" px={4}>
              {folder || '/'}
            </Text>
            .
          </Text>
        </Group>
      </Dropzone>
      {transfers.map((t) => (
        <Group key={t.name} gap="sm" wrap="nowrap">
          <Text size="xs" ff="monospace" w={260} truncate>
            {t.name}
          </Text>
          {t.error ? (
            <Text size="xs" c="red">
              {t.error}
            </Text>
          ) : (
            <Progress value={t.progress * 100} flex={1} size="sm" color={t.progress === 1 ? 'teal' : 'enamel'} />
          )}
        </Group>
      ))}
    </Stack>
  );
}

function Preview({ file, onClose }: { file: ShelfFile | null; onClose: () => void }) {
  const url = useQuery({
    queryKey: ['preview', file?.key],
    queryFn: () => previewURL(file!.key),
    enabled: !!file,
    // A signed URL expires; a new preview signs a new one.
    gcTime: 0,
  });
  const kind = file ? previewKind(file.contentType) : null;
  return (
    <Modal opened={!!file} onClose={onClose} size="xl" title={<Text ff="monospace">{file?.name}</Text>} centered>
      {url.isLoading ? <Loader size="sm" /> : null}
      {url.error ? <Alert color="red">{(url.error as Error).message}</Alert> : null}
      {url.data && kind === 'video' ? (
        <video src={url.data} controls autoPlay style={{ width: '100%', maxHeight: '70vh', background: '#000' }} />
      ) : null}
      {url.data && kind === 'audio' ? <audio src={url.data} controls autoPlay style={{ width: '100%' }} /> : null}
      {url.data && kind === 'image' ? <img src={url.data} alt={file?.name} style={{ maxWidth: '100%', maxHeight: '70vh' }} /> : null}
      {url.data && kind === 'pdf' ? (
        <iframe src={url.data} title={file?.name} style={{ width: '100%', height: '70vh', border: 0 }} />
      ) : null}
    </Modal>
  );
}
